/* eslint-disable */
import { defineStore } from 'pinia';
import { safeLocalStorage, safeSessionStorage } from '@teachino/shared-util';
import MutationCall from '~/graphql/wrappers/mutation-call';
import QueryCall from '~/graphql/wrappers/query-call';
import { deleteTokens, storeTokens } from '~/plugins/urql';

import {
  type AuthResult,
  type AuthFlowResult,
  ChangePasswordDocument,
  type ChangePasswordMutation,
  type ChangePasswordMutationVariables,
  Language,
  LoginDocument,
  type LoginMutation,
  type LoginMutationVariables,
  MailVerificationStatus,
  MeDocument,
  type MeQuery,
  RequestUserPasswordResetDocument,
  type RequestUserPasswordResetMutation,
  type RequestUserPasswordResetMutationVariables,
  RequestVerificationEmailDocument,
  type RequestVerificationEmailMutation,
  type RequestVerificationEmailMutationVariables,
  UpdateUserDocument,
  UpdateUserEmailDocument,
  type UpdateUserEmailMutation,
  type UpdateUserEmailMutationVariables,
  type UpdateUserInput,
  type UpdateUserMutation,
  type UpdateUserMutationVariables,
  type User,
  VerifyUserEmailDocument,
  type VerifyUserEmailMutation,
  type VerifyUserEmailMutationVariables,
  VerifyUserPasswordResetDocument,
  type VerifyUserPasswordResetMutation,
  type VerifyUserPasswordResetMutationVariables,
} from '~/graphql';

import useTenantInvite from '~/composables/tenant/useTenantInvite';
import { useCourseCollaborationManagement } from '~/stores/courseCollaborationManagement';
import { signOutProductlane } from '~/utils/productlane';
import { LOGIN_PATH } from '~/utils/signup';
import {
  AvailableClassregisterIntegrations,
  AvailableTimetableIntegrations,
} from '~/utils/constants';
import {
  ExportLibraryDocument,
  type ExportLibraryMutation,
  type ExportLibraryMutationVariables,
} from '../graphql';

export const TWO_FACTOR_CHALLENGE_STORAGE_KEY = 'twoFactorChallengeToken';

let loginMutationCall: MutationCall<LoginMutation, LoginMutationVariables>;
let requestVerificationEmailMutationCall: MutationCall<
  RequestVerificationEmailMutation,
  RequestVerificationEmailMutationVariables
>;
let verifyUserEmailMutationCall: MutationCall<
  VerifyUserEmailMutation,
  VerifyUserEmailMutationVariables
>;

let updateUserMutationCall: MutationCall<
  UpdateUserMutation,
  UpdateUserMutationVariables
>;

let updateUserEmailMutationCall: MutationCall<
  UpdateUserEmailMutation,
  UpdateUserEmailMutationVariables
>;
let requestUserPasswordResetCall: MutationCall<
  RequestUserPasswordResetMutation,
  RequestUserPasswordResetMutationVariables
>;
let verifyUserPasswordResetCall: MutationCall<
  VerifyUserPasswordResetMutation,
  VerifyUserPasswordResetMutationVariables
>;
let changePasswordCall: MutationCall<
  ChangePasswordMutation,
  ChangePasswordMutationVariables
>;

let exportLibraryContentCall: MutationCall<
  ExportLibraryMutation,
  ExportLibraryMutationVariables
>;
let meQueryCall: QueryCall<MeQuery>;

export class LoginError extends Error {}

export class UserConflictError extends Error {}

export class InvalidCodeError extends Error {}

export class LimitReachedError extends Error {}

export class ItemExpiredError extends Error {}

let courseCollaborationManagementStore = null;

let acceptTenantCodeAfterLogin = null;

type UserItem = {
  firstName: string;
  lastName: string;
}

export const useSession = defineStore('session', {
  state: () => ({
    loggedIn: safeLocalStorage.getItem('loggedIn') ?? false,
    user: safeLocalStorage.getItem('user')
      ? JSON.parse(safeLocalStorage.getItem('user')!)
      : null,
    activeIntegrations: [],
    licenses: [],
    prefillUsername: '',
    userLoaded: false,
  }),
  getters: {
    isLoggedIn: (state) => state.loggedIn,
    isSignedUp: (state) => !!state.user.signedUp,
    needsEmailVerification: (state) =>
      state.user.emailVerificationStatus == MailVerificationStatus.Unverified,
    hasCompletedInitialEmailVerification: (state) =>
      !!state.user?.signupEmailVerifiedAt,
    getUser: (state): UserItem => state.user,
    getUserId: (state) => state.user?.id,
    getFullName: (state) => `${state.user?.lastName} ${state.user?.firstName}`,
    getEmail: (state): string => state.user?.email,
    getInitials: (state) =>
      state.user
        ? `${state.user?.firstName.charAt(0).toUpperCase()}${state.user?.lastName
            .charAt(0)
            .toUpperCase()}`
        : '',
    getDefaultTenantId: (state) => state.user?.defaultTenant.id,
    isTeacher: (state) => state.user?.defaultTenant.permissions.isTenantTeacher,
    canCreateCourse: (state) =>
      state.user?.defaultTenant.permissions.canCreateCourse,
    getUserLanguage: (state): Language => state.user?.language ?? Language.De,
    getPrefillUsername: (state) => state.prefillUsername,
    hasActiveClassRegisterIntegration: (state) =>
      state.activeIntegrations.find(
        (integration) =>
          AvailableClassregisterIntegrations.indexOf(
            integration.externalIntegrationSetting.key,
          ) > -1,
      ) !== undefined,
    hasActiveTimetableIntegration: (state) =>
      state.activeIntegrations.find(
        (integration) =>
          AvailableTimetableIntegrations.indexOf(
            integration.externalIntegrationSetting.key,
          ) > -1,
      ) !== undefined,
    hasActiveEuropathekIntegration: (state) =>
      state.activeIntegrations.find(
        (integration) => integration.externalIntegrationSetting.key === 'europathek'
      ) !== undefined,
    getLicenses: (state) => state.licenses,
  },
  actions: {
    init() {
      loginMutationCall = new MutationCall(LoginDocument);
      requestVerificationEmailMutationCall = new MutationCall(
        RequestVerificationEmailDocument,
      );
      verifyUserEmailMutationCall = new MutationCall(VerifyUserEmailDocument);
      updateUserMutationCall = new MutationCall(UpdateUserDocument);
      updateUserEmailMutationCall = new MutationCall(UpdateUserEmailDocument);
      requestUserPasswordResetCall = new MutationCall(
        RequestUserPasswordResetDocument,
      );
      verifyUserPasswordResetCall = new MutationCall(
        VerifyUserPasswordResetDocument,
      );
      changePasswordCall = new MutationCall(ChangePasswordDocument);
      meQueryCall = new QueryCall<MeQuery>(MeDocument);
      courseCollaborationManagementStore = useCourseCollaborationManagement();
      courseCollaborationManagementStore.init();
      acceptTenantCodeAfterLogin = useTenantInvite().acceptCodeAfterLogin;
      exportLibraryContentCall = new MutationCall(ExportLibraryDocument);
    },
    setPrefillUsername(username: string) {
      this.prefillUsername = username;
    },
    logOut() {
      signOutProductlane();
      deleteTokens();
      safeLocalStorage.removeItem('user');
      safeLocalStorage.removeItem('loggedIn');
      safeLocalStorage.clear();
      this.loggedIn = false;
      this.user = null;
      this.prefillUsername = '';
      this.activeIntegrations = [];
      this.licenses = [];
      this.userLoaded = false;

      const { getMeRef } = useCurrentUser();
      getMeRef().value = null;
    },
    logOutAndRedirect() {
      this.logOut();
      window.location.href = `${LOGIN_PATH}/`;
    },
    async logIn(credentials: LoginMutationVariables) {
      const result = await loginMutationCall.run(credentials);
      if (result.hasError()) {
        if (
          result.getError()?.getCode() == 'INVALIDCREDENTIALS' ||
          result.getError()?.getCode() == 'PasswordInput'
        ) {
          throw new LoginError();
        } else {
          throw result.getError();
        }
      }
      return await this.handleAuthFlowResult(result.getData().login);
    },
    async handleAuthFlowResult(
      authenticationResult: Pick<
        AuthFlowResult,
        'authResult' | 'twoFactorChallengeToken'
      >,
    ): Promise<boolean> {
      if (authenticationResult.authResult) {
        await this.initialiseLoggedInUser(authenticationResult.authResult);
        return true;
      }
      if (authenticationResult.twoFactorChallengeToken) {
        safeSessionStorage.setItem(
          TWO_FACTOR_CHALLENGE_STORAGE_KEY,
          authenticationResult.twoFactorChallengeToken,
        );
        await navigateTo('/auth/two-factor');
        return false;
      }
      throw new Error('Authentication returned neither tokens nor a challenge');
    },
    async initialiseLoggedInUser(authResult: AuthResult) {
      storeTokens(authResult);
      this.loggedIn = true;
      await this.loadUser();
    },
    async loadUser() {
      // Accept a pending tenant invitation before loading `me`, so membership
      // and tenant-provided signup defaults are reflected in the payload.
      await acceptTenantCodeAfterLogin();

      const me = await meQueryCall.run();
      if (me.hasError()) {
        throw me.getError();
      }

      const data = me.getData();
      const user = data.me as User;
      const activeIntegrations = data.externalIntegrations;
      const licences = data.licenses;
      this.storeUser(user, activeIntegrations, licences);

      const { getMeRef } = useCurrentUser();
      getMeRef().value = data;


      safeLocalStorage.setItem(
        'loggedIn',
        JSON.stringify(this.isLoggedIn ? true : false),
      );

      // accept the course invite if it was set before the login action
      await courseCollaborationManagementStore.acceptInviteAfterLogin();
    },
    async loadUserIfNeeded() {
      if (!this.userLoaded && this.isLoggedIn) {
        await this.loadUser();
      }
    },
    storeUser(user: User, activeIntegrations: [], licenses: []) {
      this.user = user;
      this.activeIntegrations = activeIntegrations;
      this.licenses = licenses;
      this.prefillUsername = user.email;
      this.userLoaded = true;
      safeLocalStorage.setItem('user', JSON.stringify(user));
    },
    async resendVerificationEmail() {
      const result = await requestVerificationEmailMutationCall.run({});
      if (result.hasError()) {
        const error = result.getError();
        if (error?.getCode() == 'LimitReached') {
          throw new LimitReachedError();
        } else {
          throw new Error();
        }
      }
    },
    async updateUserLanguage(language: Language) {
      const result = await updateUserMutationCall.run({
        updateUserInput: {
          language,
        },
      });

      if (result.hasError()) {
        throw result.getError();
      }
      this.updateLanguage(result.getData().updateUser.user.language);
    },
    async updateUser(updateUserInput: UpdateUserInput) {
      const result = await updateUserMutationCall.run({
        updateUserInput,
      });

      if (result.hasError()) {
        throw result.getError();
      }
      this.updateLanguage(result.getData().updateUser.user.language);
    },
    updateLanguage(language: Language) {
      const updatedUser = {
        ...this.user,
        language,
      };

      this.user = updatedUser;
      safeLocalStorage.setItem('user', JSON.stringify(updatedUser));
    },
    async changePassword(existingPassword: string, newPassword: string) {
      const result = await changePasswordCall.run({
        changePasswordInput: { existingPassword, newPassword },
      });

      if (result.getError()) {
        if (result.getError()!.getCode() == 'UNAUTHENTICATED') {
          throw new LoginError();
        } else {
          throw result.getError();
        }
      }
    },

    async updateEmail(email: string) {
      const result = await updateUserEmailMutationCall.run({
        emailInput: {
          email,
        },
      });
      if (result.hasError()) {
        const error = result.getError();
        if (error?.getCode() == 'NotAllowed') {
          throw new UserConflictError();
        } else if (error?.getCode() == 'EmailVerificationLimit') {
          throw new LimitReachedError();
        } else {
          throw new Error();
        }
      }
    },
    async verifyEmail(code: string) {
      const result = await verifyUserEmailMutationCall.run({
        verificationInput: { code },
      });
      if (result.hasError()) {
        const error = result.getError();
        if (error?.getCode() == 'NotFound') {
          throw new InvalidCodeError();
        } else if (error?.getCode() == 'LimitReached') {
          throw new LimitReachedError();
        } else if (error?.getCode() == 'ItemExpired') {
          throw new ItemExpiredError();
        } else {
          throw new Error();
        }
      }
      await this.loadUser();
    },
    async requestResetPassword(email: string) {
      const result = await requestUserPasswordResetCall.run({
        emailInput: { email },
      });
      if (result.hasError()) {
        const error = result.getError();
        if (error?.getCode() == 'LimitReached') {
          throw new LimitReachedError();
        } else {
          throw new Error();
        }
      }
    },
    async exportLibraryContent() {
      const result = await exportLibraryContentCall.run();
      if (result.hasError()) {
        const error = result.getError();
        console.log(error);
        throw new Error();
      }
    },
  },
});
